import { Container, type Hex, SelectListItemRenderer } from '@acies/core';
import { CheckBox, Icon, InputField, InputFieldEvent, Label, theme } from '@acies/ui';
import { IPositions, ModelPosition, StockPosition } from '../../../../domain/positions';
import { decodeSegment } from '../../../../domain/shared';
import { getAsCurrency, getFormattedInteger } from '../../../../utils/formatters';
import { ConfiguratorModel } from '../../configurator.model';
import CalculationStatus from '../calculation-status';
import { toCountable, toPositionNumber } from '../position-field-values';
import { draftWarning, EDITABLE_FIELDS, type EditableField, editableFieldsOf, type FocusDestination, type ItemKind, initial, type PositionRowEvent, type PositionRowState, transition } from './position-item-renderer.chart';
import PositionItemRendererEvent, { type EditablePosition, type PositionChanges } from './position-item-renderer-event';

/**
 * Every state the row dresses itself for: the styled state, so hovering is not among them, since the cursor is what says the row can be acted on.
 *
 * Disabled is not among them either: a row out of reach is styled by the SelectList holding it, so the row leaves itself alone — see {@link PositionItemRenderer.setStylesForState}.
 */
type StyledState = Exclude<PositionItemRenderer['styledState'], 'disabled'>;

/**
 * What fills a row that holds the selection, state by state. A row that does not hold it has no fill in any state.
 *
 * Written out so each state can be read against what was asked for, and so the compiler asks for a value the day a state appears.
 */
const SELECTED_FILLS: Record<StyledState, Hex | null> = {
	// the keyboard on the selected row marks it as strongly as it can be marked
	focused: theme.colors.fill.focus,
	focusedPressed: theme.colors.fill.focus,
	// the quiet mark the selection leaves once the keyboard is not on the row
	idle: theme.colors.fill.weak,
	pressed: null,
};

/**
 * A row in the list of positions, which draws a position and lets a model position be edited in place.
 *
 * The row is three files, and this is the shell. The rules of editing — when the row goes into edit mode, where Tab goes, when Enter commits and when it is refused, what a price landing mid-edit does to what was typed — are in position-item-renderer.chart.ts, as a pure function from a state and an event to the next
 * state and the effects. This class turns what the DOM says into the chart's events, runs them through `transition` in #send, and carries out the effects: it shows the fields or the labels, moves the focus, prevents a key's default, dispatches the changes and toggles the selection. Besides that it owns what the chart
 * has no say in: the labels and fields themselves, how they are filled and formatted, and how the row is styled. position-item-renderer.chart.world.ts models what the row does not control, for the tests only.
 *
 * Why apart: the rules are where the cases multiply, and free of the DOM they can be walked exhaustively — position-item-renderer.chart.test.ts checks the invariants in every state the chart and the world reach together. What that cannot show is that this class follows the chart, so the conformance test in
 * position-item-renderer.test.ts plays the shortest trace to every transition Chromium can bring about against a real row and compares the two after every step. For that to mean anything, this class makes no decision of its own about editing: a rule written here instead of in the chart would show up as the row
 * departing from the chart, and would never be checked against the invariants.
 */
export default class PositionItemRenderer extends SelectListItemRenderer<IPositions> {
	#checkBox!: CheckBox<IPositions>;
	#positionLabel!: Label;
	#typeIcon!: Icon;
	#typeContainer!: Container;
	#keyLabel!: Label;
	#descriptionLabel!: Label;
	#heightLabel!: Label;
	#widthLabel!: Label;
	#countLabel!: Label;
	#litraLabel!: Label;
	#noteIcon!: Icon;
	#noteContainer!: Container;
	#calculationStatus!: CalculationStatus;
	#calculationStatusContainer!: Container;
	#totalPriceLabel!: Label;
	#menuIcon!: Icon;
	#positionInputField!: InputField;
	#widthInputField!: InputField;
	#heightInputField!: InputField;
	#quantityInputField!: InputField;

	/** Whether the fields have been put into the row. They are put in the first time it is edited, so a row nobody edits carries none. */
	#fieldsAdded: boolean;

	/** The id of the position the chart was last told about, which is what tells a new edition of the same position from another position. */
	#itemId: string | null;

	/** Where the row is in its chart. The row is a shell around position-item-renderer.chart.ts — see the class — and holds no state of its own about being edited. */
	#row: PositionRowState;

	/** Whether the keyboard is on this row, however the pointer is placed. */
	get #isFocused(): boolean {
		return this.styledState === 'focused' || this.styledState === 'focusedPressed';
	}

	public constructor() {
		super();
		this.#fieldsAdded = false;
		this.#itemId = null;
		this.#row = initial;
		this.width = 'fill';
		this.height = 32;
		this.gapHorizontal = 16;
		this.paddingHorizontal = 16;
		this.alignment = 'left';
		this.cornerRadius = 4;
		this.cursor = 'pointer';
		this.#addEditListeners();
	}

	/** Whether the row is being edited, which is whether its fields are shown in place of its labels. */
	public get editing(): boolean {
		return this.#row.mode === 'editing';
	}

	public get positionInputField(): InputField {
		if (!this.#positionInputField) {
			// where a position sits is a number, so the field takes digits and one dot, as the row that
			// creates a position does
			this.#positionInputField = editField(56);
		}
		return this.#positionInputField;
	}

	public get widthInputField(): InputField {
		if (!this.#widthInputField) {
			this.#widthInputField = measureField(72);
		}
		return this.#widthInputField;
	}

	public get heightInputField(): InputField {
		if (!this.#heightInputField) {
			this.#heightInputField = measureField(72);
		}
		return this.#heightInputField;
	}

	public get quantityInputField(): InputField {
		if (!this.#quantityInputField) {
			this.#quantityInputField = measureField(56);
		}
		return this.#quantityInputField;
	}

	/**
	 * The check box goes in and out of the tab order with the row.
	 *
	 * The SelectList holding the row makes the list one tab stop by taking every row but the selected one out of the tab order, and a row taken out takes nothing inside it along. The check box of every row was a stop of its own, so Tab went on from the selected row through all of them. The check box of the row that is
	 * the stop stays in, so the keyboard can still reach it.
	 */
	public override set tabbable(value: boolean) {
		super.tabbable = value;
		this.checkBox.tabbable = value;
	}

	public override get tabbable(): boolean {
		return super.tabbable;
	}

	public get checkBox(): CheckBox<IPositions> {
		if (!this.#checkBox) {
			this.#checkBox = new CheckBox();
		}
		return this.#checkBox;
	}

	public get positionLabel(): Label {
		if (!this.#positionLabel) {
			this.#positionLabel = new Label();
			this.#positionLabel.width = 56;
			this.#positionLabel.variant = 'dark';
		}
		return this.#positionLabel;
	}

	public get typeIcon(): Icon {
		if (!this.#typeIcon) {
			this.#typeIcon = new Icon();
		}
		return this.#typeIcon;
	}

	public get typeContainer(): Container {
		if (!this.#typeContainer) {
			this.#typeContainer = new Container();
			this.#typeContainer.width = 72;
			this.#typeContainer.paddingHorizontal = 10;
			this.#typeContainer.alignment = 'left';
			this.#typeContainer.addComponent(this.typeIcon);
		}
		return this.#typeContainer;
	}

	public get keyLabel(): Label {
		if (!this.#keyLabel) {
			this.#keyLabel = new Label();
			this.#keyLabel.width = 192;
		}
		return this.#keyLabel;
	}

	public get descriptionLabel(): Label {
		if (!this.#descriptionLabel) {
			this.#descriptionLabel = new Label();
			this.#descriptionLabel.minWidth = 88;
			this.#descriptionLabel.width = 'fill';
		}
		return this.#descriptionLabel;
	}

	public get heightLabel(): Label {
		if (!this.#heightLabel) {
			this.#heightLabel = new Label();
			this.#heightLabel.width = 72;
			this.#heightLabel.textAlignHorizontal = 'right';
		}
		return this.#heightLabel;
	}

	public get widthLabel(): Label {
		if (!this.#widthLabel) {
			this.#widthLabel = new Label();
			this.#widthLabel.width = 72;
			this.#widthLabel.textAlignHorizontal = 'right';
		}
		return this.#widthLabel;
	}

	public get countLabel(): Label {
		if (!this.#countLabel) {
			this.#countLabel = new Label();
			this.#countLabel.width = 56;
			this.#countLabel.textAlignHorizontal = 'right';
		}
		return this.#countLabel;
	}

	public get litraLabel(): Label {
		if (!this.#litraLabel) {
			this.#litraLabel = new Label();
			this.#litraLabel.width = 128;
		}
		return this.#litraLabel;
	}

	public get noteIcon(): Icon {
		if (!this.#noteIcon) {
			this.#noteIcon = new Icon('sticker-square');
		}
		return this.#noteIcon;
	}

	public get noteContainer(): Container {
		if (!this.#noteContainer) {
			this.#noteContainer = new Container();
			this.#noteContainer.width = 48;
			this.#noteContainer.alignment = 'center';
			this.#noteContainer.addComponent(this.noteIcon);
		}
		return this.#noteContainer;
	}

	public get calculationStatus(): CalculationStatus {
		if (!this.#calculationStatus) {
			this.#calculationStatus = new CalculationStatus();
		}
		return this.#calculationStatus;
	}

	public get calculationStatusContainer(): Container {
		if (!this.#calculationStatusContainer) {
			this.#calculationStatusContainer = new Container();
			this.#calculationStatusContainer.width = 48;
			this.#calculationStatusContainer.alignment = 'center';
			this.#calculationStatusContainer.addComponent(this.calculationStatus);
		}
		return this.#calculationStatusContainer;
	}

	public get totalPriceLabel(): Label {
		if (!this.#totalPriceLabel) {
			this.#totalPriceLabel = new Label();
			this.#totalPriceLabel.width = 96;
			this.#totalPriceLabel.textAlignHorizontal = 'right';
		}
		return this.#totalPriceLabel;
	}

	public get menuIcon(): Icon {
		if (!this.#menuIcon) {
			this.#menuIcon = new Icon();
			this.#menuIcon.name = 'dots-vertical';
		}
		return this.#menuIcon;
	}

	/**
	 * Being selected changes how an idle row looks, and nothing about how a focused one does.
	 *
	 * So it goes through the same dispatch the state does rather than styling the row itself. It used to reach straight for the idle styles, which set no stroke — and a row selected while it had the focus therefore lost the stroke that showed where the keyboard was. That is what the arrow keys do on every press now
	 * that the selection follows the focus, but it was there for a Tab and a Space too.
	 */
	protected override selectedChanged(): void {
		super.selectedChanged();
		this.#setStylesForState();
	}

	/** The renderer extends InteractiveContainer, so the pointer and focus states are handed to us here, and we answer the ones that are styled with the fill and stroke that mark the row. */
	protected override stateChanged(): void {
		super.stateChanged();
		this.#setStylesForState();
	}

	/**
	 * Dresses the row for the state it is in.
	 *
	 * The fill and the stroke answer different things, so they are decided apart rather than as one look per state: the fill says what is selected, the stroke says where the keyboard is. The fill is looked up rather than worked out — see {@link SELECTED_FILLS}.
	 *
	 * A disabled row is left exactly as it was found. Being out of reach is styled by the SelectList holding the row rather than by the row, so anything set here would be painted over what it decided.
	 */
	#setStylesForState(): void {
		if (this.styledState === 'disabled') {
			return;
		}
		// a row being edited is where the keyboard is even while a field has it, and the row itself is
		// not focused then: the field shows its own focus, so the row is marked by its fill alone
		if (this.editing) {
			this.fill = theme.colors.fill.focus;
			this.stroke = this.#isFocused ? theme.strokes.focus_inside : null;
			return;
		}
		this.fill = this.selected ? SELECTED_FILLS[this.styledState] : null;
		// the only mark the keyboard makes, and it is made the same way whatever the fill is doing
		this.stroke = this.#isFocused ? theme.strokes.focus_inside : null;
	}

	/**
	 * Says whether the position is waiting on the user to answer a question, from the configuration it holds and whether one is being asked for — see `ConfiguratorModel.positionsConfiguring`.
	 *
	 * A model and a stock position hold a configuration, and only one the engine calls pending is waiting on an answer — and only while nothing is out for it. While one is, the position is being worked on rather than waiting, and the row shows its status as it does for any other work: a configuration asked for with an
	 * answer, or in a configuration of the item picked for it, has not come back to say whether anything is still open.
	 *
	 * Called by the row when its position changes, and by `PositionsList` when a configuration goes out or comes back, which changes no position.
	 */
	public configuringChanged(): void {
		const item = this.item;
		this.calculationStatus.awaitingAnswers = item !== null && item.type !== 'text' && item.configuration?.statusType === 'pending' && !ConfiguratorModel.positionsConfiguring.value.has(item.id);
	}

	protected override itemChanged(): void {
		super.itemChanged();
		if (this.item) {
			this.positionLabel.text = String(this.item.position);
			this.#setTypeIconNameFromType(this.item);
			// as a user reads it, since the key is kept as the engine wrote it into the path — and a text
			// position described by hand has none
			this.keyLabel.text = decodeSegment(this.item.key ?? '');
			this.descriptionLabel.text = this.item.description ?? '';
			this.#setHeightLabelTextFromType(this.item);
			this.#setWidthLabelTextFromType(this.item);
			this.#setCountLabelTextFromType(this.item);
			this.#setLitraLabelTextFromType(this.item);
			this.#setNoteIconVisibleFromType(this.item);
			this.calculationStatus.status = this.item.status;
			this.configuringChanged();
			this.totalPriceLabel.text = getAsCurrency(this.item.totalPrice);
		} else {
			this.countLabel.text = '';
			this.descriptionLabel.text = '';
			this.heightLabel.text = '';
			this.keyLabel.text = '';
			this.litraLabel.text = '';
			this.noteIcon.visible = false;
			this.positionLabel.text = '';
			this.totalPriceLabel.text = '';
			this.calculationStatus.status = 'succeeded';
			this.calculationStatus.awaitingAnswers = false;
			this.typeIcon.name = null;
			this.widthLabel.text = '';
		}
		this.#itemToChart();
	}

	/**
	 * Clicked by the pointer, or by Space or Enter released while the row itself has focus, and handed to the chart rather than answered here.
	 *
	 * What a SelectListItemRenderer does with a click — toggle the selection — is done only when the chart says so: Enter on a model position edits it, and a row being edited is not selected by a click into it.
	 */
	protected override clicked(event: Event): void {
		const by = !(event instanceof KeyboardEvent) ? 'pointer' : event.code === 'Space' ? 'space' : 'enter';
		this.#send({ by, type: 'activated' }, event);
	}

	/**
	 * Every way the row can be edited arrives as an event that bubbles to it, so one listener of each kind on the row hears all of them: focus leaving anything inside it, the keys it answers, and the fields' own events.
	 *
	 * None needs binding: `this` is the currentTarget, which is this renderer.
	 */
	#addEditListeners(): void {
		this.addEventListener('focusout', this.#focusOut);
		this.addEventListener('keydown', this.#keyDown);
		this.addEventListener(InputFieldEvent.ENTER, this.#fieldEntered as EventListener);
	}

	/**
	 * The model or stock position being edited.
	 *
	 * Only those two kinds are ever edited — see `editingOnlyForEditableKinds` in position-item-renderer.chart.test.ts, which the walk over the chart checks in every state — so this is only asked while the row draws one, and says so rather than asking again at every turn.
	 */
	get #edited(): EditablePosition {
		return this.item as EditablePosition;
	}

	/** The fields the row shows while it is edited: the kind's own, or none while it is read. A stock position is counted and placed, and has no width or height to edit. */
	get #shownFields(): ReadonlyArray<EditableField> {
		return this.#row.mode === 'editing' ? editableFieldsOf(this.#row.item) : [];
	}

	/**
	 * Runs an event through the chart and carries out what comes back.
	 *
	 * The state is taken before anything is carried out, because moving the focus is itself a focusout the row hears, and that one has to meet the new state. The order is the one that never leaves focus on a hidden field: the fields are shown, the focus is moved, and only then is a field hidden. The changes are
	 * dispatched last, once the row reads its item again, so a machine that answers at once meets a row that is done.
	 */
	#send(event: PositionRowEvent, origin?: Event): void {
		const before = this.#row;
		const { effects, state } = transition(before, event);
		this.#row = state;
		if (effects.preventDefault) {
			origin?.preventDefault();
		}
		const changes = effects.commit === null ? null : this.#changesOf(effects.commit);
		if (before.mode === 'reading' && state.mode === 'editing') {
			this.#startEditing();
			// a hidden field cannot take the focus, so the fields are shown before it is moved
			this.#showEditing();
		}
		if (effects.focus !== null) {
			(effects.focus === 'row' ? this : this.#fieldFor(effects.focus)).focus();
		}
		this.#showEditing();
		if (changes !== null) {
			this.dispatchEvent(new PositionItemRendererEvent(this.#edited, changes));
		}
		if (effects.toggleSelection && origin !== undefined) {
			super.clicked(origin);
		}
	}

	/** Puts the fields into the row the first time it is edited, and fills them with what the position holds now. */
	#startEditing(): void {
		if (!this.#fieldsAdded) {
			this.#fieldsAdded = true;
			for (const field of EDITABLE_FIELDS) {
				// right after the label the field stands in for, so it takes the label's place in the row
				const label = this.#labelFor(field);
				const input = this.#fieldFor(field);
				this.addComponentAt(Array.prototype.indexOf.call(this.children, label) + 1, input);
				// one listener per field, so what was typed is judged by the rule of the field it was typed in
				input.addEventListener(InputFieldEvent.CHANGED, (event) => {
					this.#fieldChanged(field, event);
				});
			}
		}
		for (const field of this.#shownFields) {
			this.#fieldFor(field).text = String(heldIn(this.#edited, field));
		}
	}

	/** Shows the fields or the labels, and the warning, for the state the row is in now: the fields the kind is edited in in place of their labels, and every other label as it is. */
	#showEditing(): void {
		const shown = this.#shownFields;
		if (this.#fieldsAdded) {
			for (const field of EDITABLE_FIELDS) {
				const editing = shown.includes(field);
				this.#fieldFor(field).visible = editing;
				this.#labelFor(field).visible = !editing;
			}
		}
		this.calculationStatus.draftInvalid = draftWarning(this.#row);
		this.#setStylesForState();
	}

	/**
	 * Tells the chart what the list has handed the row: a new edition of the position it is editing, or another position altogether.
	 *
	 * A new edition keeps the edit going. The fields the user has not touched are filled with what it holds, and the ones they have typed in are compared with it, since a value that was a change may not be one any more.
	 */
	#itemToChart(): void {
		const item = this.item;
		const id = item === null ? null : item.id;
		if (this.#row.mode === 'editing' && (item instanceof ModelPosition || item instanceof StockPosition) && id === this.#itemId) {
			const { draft } = this.#row;
			const shown = this.#shownFields;
			for (const field of shown) {
				if (draft[field] === 'untouched') {
					this.#fieldFor(field).text = String(heldIn(item, field));
				}
			}
			// a field the kind has no use for is never typed in, so it never differs
			const differs = Object.fromEntries(EDITABLE_FIELDS.map((field) => [field, shown.includes(field) && this.#typedValue(field) !== heldIn(item, field)])) as Record<EditableField, boolean>;
			this.#send({ differs, type: 'itemReplaced' });
		} else {
			this.#send({ item: kindOf(item), type: 'itemChanged' });
		}
		this.#itemId = id;
	}

	/** What a field's text reads as, by the rule for that field, or null when it reads as nothing. */
	#typedValue(field: EditableField): number | null {
		const text = this.#fieldFor(field).text;
		return field === 'position' ? toPositionNumber(text) : toCountable(text);
	}

	/** The changed values, read off the fields the chart says were changed. A changed field is a valid one, so each reads as a number. */
	#changesOf(fields: ReadonlyArray<EditableField>): PositionChanges {
		const values = fields.map((field) => [field, this.#typedValue(field)] as const);
		return Object.fromEntries(values.filter((entry): entry is readonly [EditableField, number] => entry[1] !== null));
	}

	/**
	 * Judges what the user typed in a field: invalid, the same as the position holds, or a change.
	 *
	 * The event is stopped here, since the row answers it — see CLAUDE.md. A place in the list another position has is invalid, the way the row that creates a position judges it.
	 */
	#fieldChanged(field: EditableField, event: Event): void {
		event.stopPropagation();
		const { id } = this.#edited;
		const value = this.#typedValue(field);
		const taken = field === 'position' && ConfiguratorModel.positions.collection.some((position) => position.position === value && position.id !== id);
		this.#send({ differs: value !== heldIn(this.#edited, field), field, type: 'fieldTyped', valid: value !== null && !taken });
	}

	/** Enter released in a field. Stopped here, since the row answers it. */
	#fieldEntered(event: InputFieldEvent): void {
		event.stopPropagation();
		this.#send({ type: 'fieldEntered' });
	}

	/** Where the focus went as it left something inside the row: still inside it, out of it, or nowhere, which is the window losing focus. */
	#focusOut(event: FocusEvent): void {
		const { relatedTarget } = event;
		let to: FocusDestination = 'outside';
		if (relatedTarget instanceof Node && this.contains(relatedTarget)) {
			to = 'inside';
		} else if (!document.hasFocus()) {
			to = 'nowhere';
		}
		this.#send({ to, type: 'focusLeft' });
	}

	/** The keys the row answers wherever in it they are pressed: F2, Escape and Tab. */
	#keyDown(event: KeyboardEvent): void {
		if (event.code === 'F2') {
			this.#send({ type: 'editRequested' }, event);
		} else if (event.code === 'Escape') {
			this.#send({ type: 'escapePressed' }, event);
		} else if (event.code === 'Tab') {
			this.#send({ backwards: event.shiftKey, from: this.#fieldHolding(event.target), type: 'tabPressed' }, event);
		}
	}

	/** The field an element is, or is inside, or null when it is in none of them. */
	#fieldHolding(target: EventTarget | null): EditableField | null {
		if (!(target instanceof Node) || !this.#fieldsAdded) {
			return null;
		}
		return EDITABLE_FIELDS.find((field) => this.#fieldFor(field).contains(target)) ?? null;
	}

	#fieldFor(field: EditableField): InputField {
		switch (field) {
			case 'height':
				return this.heightInputField;
			case 'position':
				return this.positionInputField;
			case 'quantity':
				return this.quantityInputField;
			case 'width':
				return this.widthInputField;
		}
	}

	/** The label a field stands in for while the row is edited. */
	#labelFor(field: EditableField): Label {
		switch (field) {
			case 'height':
				return this.heightLabel;
			case 'position':
				return this.positionLabel;
			case 'quantity':
				return this.countLabel;
			case 'width':
				return this.widthLabel;
		}
	}

	#setTypeIconNameFromType(value: IPositions): void {
		if (value.type === 'model') {
			this.typeIcon.name = 'layout-grid-01';
		} else if (value.type === 'stock') {
			this.typeIcon.name = 'package';
		} else {
			this.typeIcon.name = 'type-01';
		}
	}
	// a model position is the only kind made to a size: the others are what they are
	#setHeightLabelTextFromType(value: IPositions) {
		this.heightLabel.text = value.type === 'model' ? getFormattedInteger(value.height) : '';
	}

	#setWidthLabelTextFromType(value: IPositions) {
		this.widthLabel.text = value.type === 'model' ? getFormattedInteger(value.width) : '';
	}

	// a text position is one line of text however many it is said to be, so it is not counted in
	// the list — the row that creates one asks for no count either
	#setCountLabelTextFromType(value: IPositions) {
		this.countLabel.text = value.type === 'text' ? '' : getFormattedInteger(value.quantity);
	}

	#setLitraLabelTextFromType(value: IPositions) {
		if (value.type === 'model') {
			this.litraLabel.text = value.litra ?? '';
		} else {
			this.litraLabel.text = '';
		}
	}

	#setNoteIconVisibleFromType(value: IPositions) {
		if (value.type === 'model') {
			this.noteIcon.visible = Boolean(value.note);
		} else {
			this.noteIcon.visible = false;
		}
	}

	protected override initialize(): void {
		super.initialize();
		this.addComponents([this.checkBox, this.positionLabel, this.typeContainer, this.keyLabel, this.descriptionLabel, this.widthLabel, this.heightLabel, this.countLabel, this.litraLabel, this.noteContainer, this.calculationStatusContainer, this.totalPriceLabel, this.menuIcon]);
	}
}

/**
 * A field a position is edited in, the way the row that creates a position has one.
 *
 * It keeps the focus after Enter, because Enter can be refused: a row that will not save an invalid value has the user go on typing in the same field. Hidden until the row is edited, so it takes no place in the row and cannot be tabbed to.
 *
 * Its text is selected as it takes the focus, by F2, Tab or a click, because a number in a row is typed again whole rather than edited: the user types the new one straight over the old, without deleting it first.
 */
function editField(width: number): InputField {
	const field = new InputField();
	field.blurOnEnter = false;
	field.selectOnFocus = true;
	field.negatives = false;
	field.type = 'numeric';
	field.visible = false;
	field.width = width;
	return field;
}

/** A field for a measurement or a quantity: whole numbers, right aligned like the column it stands in, as in the row that creates a position. */
function measureField(width: number): InputField {
	const field = editField(width);
	field.decimals = false;
	field.textAlign = 'right';
	return field;
}

/** The kind of position the chart is told the row draws: a model and a stock position are the two it edits, and a text position is another kind. */
function kindOf(item: IPositions | null): ItemKind {
	if (item === null) {
		return 'none';
	}
	return item.type === 'text' ? 'other' : item.type;
}

/**
 * What a position being edited holds in one of the fields it is edited in.
 *
 * A stock position is edited in its place and its count alone — see `editableFieldsOf` — and the row only ever asks about the fields it shows, so those are the two a stock position is asked for.
 */
function heldIn(position: EditablePosition, field: EditableField): number {
	if (position instanceof ModelPosition) {
		return position[field];
	}
	return field === 'position' ? position.position : position.quantity;
}
customElements.define('cf-position-item-renderer', PositionItemRenderer);
